<template>
  <div class="feedback-table-container">
    <!-- 顶部筛选栏 -->
    <div class="filter-bar">
      <form class="filter-form">
        <div class="form-item">
          <label>解决状态:</label>
          <select v-model="filterForm.ifSolved" class="form-select">
            <option value="">全部</option>
            <option value="0">未解决</option>
            <option value="1">已解决</option>
          </select>
        </div>

        <div class="form-item">
          <label>反馈类型:</label>
          <select v-model="filterForm.feedbackType" class="form-select">
            <option value="">全部</option>
            <option value="0">单词音标或发音</option>
            <option value="1">单词词性或释义</option>
            <option value="2">AI对话内容</option>
            <option value="3">其他</option>
            <option value="4">功能建议</option>
            <option value="5">bug反馈</option>
            <option value="6">声线许愿</option>
          </select>
        </div>

        <div class="form-item">
          <button
            type="button"
            class="btn btn-primary"
            @click="loadFeedbacks"
            :disabled="loading"
          >
            {{ loading ? "查询中..." : "查询" }}
          </button>
        </div>
      </form>

      <!-- 分页控制 -->
      <div class="pagination-controls">
        <button
          type="button"
          class="btn btn-pagination"
          :disabled="currentPage <= 1"
          @click="previousPage"
        >
          上一页
        </button>
        <span class="page-info">{{ currentPage }} / {{ totalPages }}</span>
        <button
          type="button"
          class="btn btn-pagination"
          :disabled="currentPage >= totalPages"
          @click="nextPage"
        >
          下一页
        </button>
      </div>
    </div>

    <!-- 反馈列表 -->
    <div class="feedback-content">
      <div class="feedback-list-card">
        <div class="card-header">
          <span>用户反馈工单</span>
          <span class="feedback-count">共 {{ total }} 条反馈</span>
        </div>

        <div class="card-body">
          <div class="feedback-list" :class="{ loading: loading }">
            <div
              v-for="feedback in feedbackList"
              :key="feedback.id"
              class="feedback-item"
              :class="{ solved: feedback.ifSolved === 1 }"
            >
              <div class="feedback-header">
                <div class="feedback-id">#{{ feedback.id }}</div>
                <div class="feedback-status">
                  <span
                    :class="[
                      'status-badge',
                      feedback.ifSolved === 1
                        ? 'status-solved'
                        : 'status-pending',
                    ]"
                  >
                    {{ feedback.ifSolved === 1 ? "已解决" : "未解决" }}
                  </span>
                </div>
                <div class="feedback-time">{{ feedback.createTime }}</div>
              </div>

              <div class="feedback-body">
                <div class="feedback-content-text">
                  {{ feedback.feedbackContent }}
                </div>
                <div class="feedback-meta">
                  <span class="user-id">用户ID: {{ feedback.userId }}</span>
                  <span
                    v-if="feedback.feedbackType !== undefined"
                    class="feedback-type"
                  >
                    类型: {{ getFeedbackTypeName(feedback.feedbackType) }}
                  </span>
                </div>
              </div>

              <div class="feedback-actions" v-if="feedback.ifSolved === 0">
                <button
                  v-if="Number(feedback.feedbackType) === 0"
                  type="button"
                  class="btn btn-info btn-small"
                  @click="openWordFeedback(feedback)"
                >
                  立即查看
                </button>
                <button
                  type="button"
                  class="btn btn-primary btn-small"
                  @click="openReplyDialog(feedback)"
                >
                  回复用户
                </button>
                <button
                  type="button"
                  class="btn btn-warning btn-small"
                  @click="openGiftDialog(feedback)"
                  :disabled="gifting"
                >
                  {{
                    gifting && currentGiftFeedback?.id === feedback.id
                      ? "赠送中..."
                      : "赠送礼物"
                  }}
                </button>
                <button
                  type="button"
                  class="btn btn-success btn-small"
                  @click="markAsSolved(feedback)"
                >
                  标记为已解决
                </button>
              </div>
            </div>

            <div
              v-if="feedbackList.length === 0 && !loading"
              class="empty-state"
            >
              <div class="empty-content">
                <div class="empty-icon">📋</div>
                <div class="empty-text">暂无反馈数据</div>
              </div>
            </div>

            <div v-if="loading" class="loading-state">
              <div class="loading-spinner"></div>
              <div class="loading-text">加载中...</div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 回信用户弹窗 -->
    <div
      v-if="replyDialogVisible"
      class="dialog-overlay"
      @click="closeReplyDialog"
    >
      <div class="dialog-content" @click.stop>
        <div class="dialog-header">
          <h3>回复用户</h3>
          <button class="dialog-close" @click="closeReplyDialog">×</button>
        </div>
        <div class="dialog-body">
          <div class="form-group">
            <label>回复内容：</label>
            <textarea
              v-model="replyForm.replyContent"
              class="form-textarea"
              placeholder="请输入回复内容"
              rows="5"
            ></textarea>
          </div>
        </div>
        <div class="dialog-footer">
          <button
            type="button"
            class="btn btn-secondary"
            @click="closeReplyDialog"
          >
            取消
          </button>
          <button
            type="button"
            class="btn btn-primary"
            @click="sendNotice"
            :disabled="sending"
          >
            {{ sending ? "发送中..." : "回复" }}
          </button>
        </div>
      </div>
    </div>

    <!-- 赠送礼物弹窗 -->
    <div
      v-if="giftDialogVisible"
      class="dialog-overlay"
      @click="closeGiftDialog"
    >
      <div class="dialog-content" @click.stop>
        <div class="dialog-header">
          <h3>赠送礼物</h3>
          <button class="dialog-close" @click="closeGiftDialog">×</button>
        </div>
        <div class="dialog-body">
          <div class="gift-info">
            <p>
              确定要给用户ID:
              <strong>{{ currentGiftFeedback?.userId }}</strong> 赠送礼物吗？
            </p>
          </div>
        </div>
        <div class="dialog-footer">
          <button
            type="button"
            class="btn btn-secondary"
            @click="closeGiftDialog"
          >
            取消
          </button>
          <button
            type="button"
            class="btn btn-warning"
            @click="confirmGiftScore"
            :disabled="gifting"
          >
            {{ gifting ? "赠送中..." : "确认赠送" }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { feedback } from "../../../api/index";

export default {
  name: "FeedbackTable",
  data() {
    return {
      loading: false,
      filterForm: {
        ifSolved: "0", // 默认显示未解决的
        feedbackType: "", // 默认显示所有类型
      },
      feedbackList: [],
      total: 0,
      currentPage: 1,
      pageSize: 10,
      replyDialogVisible: false,
      sending: false,
      currentFeedback: null,
      replyForm: {
        replyContent: "",
      },
      giftDialogVisible: false,
      gifting: false,
      currentGiftFeedback: null,
    };
  },

  computed: {
    totalPages() {
      return Math.ceil(this.total / this.pageSize);
    },
  },

  created() {
    this.loadFeedbacks();
  },

  methods: {
    parseWordFeedback(feedbackContent) {
      if (!feedbackContent) return null;

      const match = String(feedbackContent)
        .trim()
        .match(/(.+?)\((\d+)\)\s*\/\s*(.+?)\((\d+)\)/);

      if (!match) return null;

      return {
        idolName: match[1]?.trim() || "",
        idolId: Number(match[2]),
        content: match[3]?.trim() || "",
        wordId: Number(match[4]),
      };
    },

    openWordFeedback(feedbackItem) {
      const parsed = this.parseWordFeedback(feedbackItem.feedbackContent);

      if (!parsed) {
        this.$message.error("当前反馈内容无法解析跳转信息");
        return;
      }

      const routeData = this.$router.resolve({
        path: "/wordTable",
        query: {
          idolId: String(parsed.idolId),
          content: parsed.content,
          wordId: String(parsed.wordId),
        },
      });

      window.open(routeData.href, "_blank");
    },

    async loadFeedbacks() {
      this.loading = true;
      try {
        const requestData = {
          pageNum: this.currentPage,
          pageSize: this.pageSize,
        };

        // 添加筛选参数（确保数据类型正确）
        if (this.filterForm.ifSolved !== "") {
          requestData.ifSolved = parseInt(this.filterForm.ifSolved);
        }
        if (this.filterForm.feedbackType !== "") {
          requestData.feedbackType = parseInt(this.filterForm.feedbackType);
        }

        console.log("请求参数:", requestData);
        console.log(
          "筛选条件 - 解决状态:",
          this.filterForm.ifSolved,
          "反馈类型:",
          this.filterForm.feedbackType
        );

        const response = await feedback.showFeedbackList(requestData);

        console.log("API返回数据:", response);
        if (response.code === 200) {
          // 检查API是否正确处理了筛选参数
          console.log(
            "API返回的所有feedbackType组:",
            response.data.data?.map((group) => group.feedbackType)
          );

          // 处理嵌套的数据结构
          let allFeedbacks = [];
          if (response.data.data && response.data.data.length > 0) {
            // 合并所有feedbackType下的feedbacks
            response.data.data.forEach((group) => {
              console.log(
                `处理feedbackType: ${group.feedbackType}, 数据条数: ${
                  group.feedbacks?.length || 0
                }`
              );
              if (group.feedbacks && group.feedbacks.length > 0) {
                // 为每个feedback添加feedbackType字段
                const feedbacksWithType = group.feedbacks.map((feedback) => ({
                  ...feedback,
                  feedbackType: group.feedbackType,
                }));
                allFeedbacks = allFeedbacks.concat(feedbacksWithType);
              }
            });
          } else {
            console.log("API返回空数据或无匹配结果");
          }

          this.feedbackList = allFeedbacks;
          this.total = response.data.total || 0;

          // 临时方案：如果API没有正确筛选，在前端做二次筛选
          if (this.filterForm.feedbackType !== "" && allFeedbacks.length > 0) {
            const expectedType = parseInt(this.filterForm.feedbackType);
            const hasExpectedType = allFeedbacks.some(
              (item) => item.feedbackType === expectedType
            );

            if (!hasExpectedType) {
              console.warn(
                `警告：API未返回期望的feedbackType(${expectedType})数据，当前返回的类型:`,
                [...new Set(allFeedbacks.map((item) => item.feedbackType))]
              );
              // 如果API没有返回期望的类型，清空列表
              this.feedbackList = [];
              this.total = 0;
            }
          }

          console.log(
            `加载第${this.currentPage}页数据，共${this.feedbackList.length}条`
          );
          console.log("解析后的反馈列表:", this.feedbackList);

          // 验证筛选结果
          if (this.feedbackList.length > 0) {
            console.log(
              "数据验证 - 第一条反馈的类型:",
              this.feedbackList[0].feedbackType,
              "对应名称:",
              this.getFeedbackTypeName(this.feedbackList[0].feedbackType)
            );
          }
        } else {
          this.$message.error(response.msg || "加载反馈失败");
        }
      } catch (error) {
        console.error("加载反馈失败:", error);
        this.$message.error("加载反馈失败");
      } finally {
        this.loading = false;
      }
    },

    async markAsSolved(feedbackItem) {
      try {
        console.log("标记为已解决:", { id: feedbackItem.id, ifSolved: 1 });

        const response = await feedback.updateFeedback({
          id: feedbackItem.id,
          ifSolved: 1,
        });

        console.log("更新反馈响应:", response);

        if (response.code === 200) {
          // 更新本地数据
          feedbackItem.ifSolved = 1;
          this.$message.success("已标记为已解决");

          // 如果当前筛选条件是"未解决"，重新加载数据
          if (this.filterForm.ifSolved === "0") {
            this.loadFeedbacks();
          }
        } else {
          this.$message.error(response.msg || "更新失败");
        }
      } catch (error) {
        console.error("标记失败:", error);
        this.$message.error("操作失败");
      }
    },

    getFeedbackTypeName(type) {
      const typeMap = {
        0: "单词音标或发音",
        1: "单词词性或释义",
        2: "AI对话内容",
        3: "其他",
        4: "功能建议",
        5: "bug反馈",
        6: "声线许愿",
      };
      return typeMap[type] || "未知类型";
    },

    onFilterChange() {
      this.currentPage = 1;
      this.loadFeedbacks();
    },

    previousPage() {
      if (this.currentPage > 1) {
        this.currentPage--;
        console.log(`切换到第${this.currentPage}页`);
        this.loadFeedbacks();
      }
    },

    nextPage() {
      if (this.currentPage < this.totalPages) {
        this.currentPage++;
        console.log(`切换到第${this.currentPage}页`);
        this.loadFeedbacks();
      }
    },

    // 打开回信弹窗
    openReplyDialog(feedbackItem) {
      this.currentFeedback = feedbackItem;
      this.replyForm.replyContent = "";
      this.replyDialogVisible = true;
    },

    // 关闭回信弹窗
    closeReplyDialog() {
      this.replyDialogVisible = false;
      this.currentFeedback = null;
      this.replyForm.replyContent = "";
    },

    // 发送回复
    async sendNotice() {
      // 验证表单
      if (!this.replyForm.replyContent.trim()) {
        this.$message.warning("请输入回复内容");
        return;
      }

      this.sending = true;
      try {
        const response = await feedback.sendNotice({
          id: this.currentFeedback.id,
          replyContent: this.replyForm.replyContent.trim(),
        });

        if (response.code === 200) {
          this.$message.success("回复发送成功");
          this.closeReplyDialog();
        } else {
          this.$message.error(response.msg || "发送失败");
        }
      } catch (error) {
        console.error("发送回复失败:", error);
        this.$message.error("发送失败，请重试");
      } finally {
        this.sending = false;
      }
    },

    // 打开赠送礼物弹窗
    openGiftDialog(feedbackItem) {
      this.currentGiftFeedback = feedbackItem;
      this.giftDialogVisible = true;
    },

    // 关闭赠送礼物弹窗
    closeGiftDialog() {
      this.giftDialogVisible = false;
      this.currentGiftFeedback = null;
    },

    // 确认赠送礼物
    async confirmGiftScore() {
      if (!this.currentGiftFeedback) {
        this.$message.error("未选择用户");
        return;
      }

      this.gifting = true;
      try {
        const response = await feedback.giftScore({
          userId: this.currentGiftFeedback.userId,
        });

        if (response.code === 200) {
          this.$message.success("礼物赠送成功");
          this.closeGiftDialog();
        } else {
          this.$message.error(response.msg || "赠送失败");
        }
      } catch (error) {
        console.error("赠送礼物失败:", error);
        this.$message.error("赠送失败，请重试");
      } finally {
        this.gifting = false;
      }
    },
  },

  watch: {
    "filterForm.ifSolved"() {
      this.onFilterChange();
    },
    "filterForm.feedbackType"() {
      this.onFilterChange();
    },
  },
};
</script>

<style scoped>
.feedback-table-container {
  height: 100%;
  display: flex;
  flex-direction: column;
  background-color: #faf9de;
  overflow: hidden;
}

.filter-bar {
  background: #fff;
  padding: 16px 20px;
  border-bottom: 1px solid #e4e7ed;
  display: flex;
  justify-content: space-between;
  align-items: center;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  border-radius: 12px;
  margin: 10px;
  flex-shrink: 0;
}

.filter-form {
  margin: 0;
  display: flex;
  align-items: center;
  gap: 20px;
  flex-wrap: wrap;
}

.form-item {
  display: flex;
  align-items: center;
  gap: 8px;
}

.form-item label {
  font-weight: 600;
  color: #2c3e50;
  font-size: 14px;
  white-space: nowrap;
}

/* 按钮样式 */
.btn {
  padding: 8px 16px;
  border: 1px solid;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
}

.btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  transform: none !important;
}

.btn-primary {
  background-color: #ffd700;
  border-color: #ffd700;
  color: #000;
}

.btn-primary:hover:not(:disabled) {
  background-color: #e6c200;
  border-color: #e6c200;
  transform: translateY(-1px);
}

.btn-pagination {
  background-color: #ffd700;
  border-color: #ffd700;
  color: #000;
}

.btn-pagination:hover:not(:disabled) {
  background-color: #e6c200;
  border-color: #e6c200;
  transform: translateY(-1px);
}

.btn-pagination:disabled {
  background-color: #ccc;
  border-color: #ccc;
  color: #888;
}

.btn-small {
  padding: 4px 8px;
  font-size: 12px;
}

.btn-success {
  background-color: #67c23a;
  border-color: #67c23a;
  color: white;
}

.btn-success:hover:not(:disabled) {
  background-color: #5daf34;
  border-color: #5daf34;
}

.btn-warning {
  background-color: #e6a23c;
  border-color: #e6a23c;
  color: white;
}

.btn-warning:hover:not(:disabled) {
  background-color: #cf9236;
  border-color: #cf9236;
}

.pagination-controls {
  display: flex;
  align-items: center;
  gap: 12px;
}

.page-info {
  font-size: 14px;
  color: #2c3e50;
  font-weight: 600;
  min-width: 60px;
  text-align: center;
}

/* 表单元素样式 */
.form-select {
  padding: 8px 12px;
  border: 1px solid #dcdfe6;
  border-radius: 6px;
  font-size: 14px;
  transition: border-color 0.3s ease;
  font-family: inherit;
  cursor: pointer;
  background-color: white;
  color: #2c3e50;
  min-width: 120px;
}

.form-select:focus {
  outline: none;
  border-color: #ffd700;
  box-shadow: 0 0 0 2px rgba(255, 215, 0, 0.2);
}

.feedback-content {
  flex: 1;
  padding: 10px;
  overflow: hidden;
  min-height: 0;
}

.feedback-list-card {
  height: 100%;
  display: flex;
  flex-direction: column;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  border: none;
  background: white;
}

.card-header {
  background: linear-gradient(135deg, #ffd700 0%, #ffed4a 100%);
  border-radius: 12px 12px 0 0;
  padding: 16px 20px;
  border-bottom: 1px solid #e6e6e6;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.card-header span {
  font-size: 16px;
  font-weight: 600;
  color: #2c3e50;
}

.feedback-count {
  font-size: 12px;
  color: #666;
  background: #ff6b35;
  padding: 4px 8px;
  border-radius: 12px;
  color: white !important;
  font-weight: 500;
}

.card-body {
  flex: 1;
  overflow: hidden;
  padding: 0;
}

.feedback-list {
  height: 100%;
  overflow-y: auto;
  padding: 16px;
}

.feedback-item {
  background: white;
  border: 1px solid #e4e7ed;
  border-radius: 12px;
  margin-bottom: 16px;
  padding: 16px;
  transition: all 0.3s ease;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.feedback-item:hover {
  border-color: #ffd700;
  box-shadow: 0 4px 12px rgba(255, 215, 0, 0.2);
  transform: translateY(-1px);
}

.feedback-item.solved {
  background-color: #f0f9ff;
  border-color: #67c23a;
}

.feedback-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
  padding-bottom: 8px;
  border-bottom: 1px solid #f0f0f0;
}

.feedback-id {
  font-size: 14px;
  font-weight: 600;
  color: #666;
}

.feedback-status {
  display: flex;
  align-items: center;
}

.status-badge {
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
}

.status-pending {
  background-color: #fff3cd;
  color: #856404;
  border: 1px solid #ffeaa7;
}

.status-solved {
  background-color: #d4edda;
  color: #155724;
  border: 1px solid #c3e6cb;
}

.feedback-time {
  font-size: 12px;
  color: #999;
}

.feedback-body {
  margin-bottom: 12px;
}

.feedback-content-text {
  font-size: 14px;
  color: #2c3e50;
  line-height: 1.5;
  margin-bottom: 8px;
}

.feedback-meta {
  display: flex;
  gap: 16px;
  font-size: 12px;
  color: #666;
}

.user-id {
  background: #f8f9fa;
  padding: 2px 6px;
  border-radius: 4px;
}

.feedback-type {
  background: #e3f2fd;
  color: #1976d2;
  padding: 2px 6px;
  border-radius: 4px;
  font-weight: 500;
}

.feedback-actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  padding-top: 8px;
  border-top: 1px solid #f0f0f0;
}

/* 加载和空状态样式 */
.loading-state,
.empty-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  color: #666;
}

.loading-spinner {
  width: 20px;
  height: 20px;
  border: 2px solid #f3f3f3;
  border-top: 2px solid #ffd700;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 10px;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

.loading-text,
.empty-text {
  font-size: 14px;
  margin-top: 8px;
}

.empty-icon {
  font-size: 32px;
  margin-bottom: 8px;
}

/* 滚动条样式 */
.feedback-list::-webkit-scrollbar {
  width: 6px;
}

.feedback-list::-webkit-scrollbar-track {
  background: rgba(255, 255, 255, 0.1);
  border-radius: 10px;
}

.feedback-list::-webkit-scrollbar-thumb {
  background: rgba(255, 215, 0, 0.3);
  border-radius: 10px;
}

.feedback-list::-webkit-scrollbar-thumb:hover {
  background: rgba(255, 215, 0, 0.5);
}

/* 响应式设计 */
@media (max-width: 1200px) {
  .filter-bar {
    flex-direction: column;
    gap: 16px;
    align-items: stretch;
  }

  .pagination-controls {
    justify-content: center;
  }
}

@media (max-width: 768px) {
  .filter-bar {
    margin: 5px;
    padding: 12px;
  }

  .feedback-content {
    padding: 5px;
  }

  .feedback-item {
    padding: 12px;
  }

  .feedback-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }

  .feedback-actions {
    justify-content: flex-start;
  }
}

/* 弹窗样式 */
.dialog-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.dialog-content {
  background: white;
  border-radius: 12px;
  width: 90%;
  max-width: 500px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
  display: flex;
  flex-direction: column;
  max-height: 90vh;
}

.dialog-header {
  padding: 20px;
  border-bottom: 1px solid #e4e7ed;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-radius: 12px 12px 0 0;
  background: linear-gradient(135deg, #ffd700 0%, #ffed4a 100%);
}

.dialog-header h3 {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: #2c3e50;
}

.dialog-close {
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: #666;
  padding: 0;
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: all 0.3s ease;
}

.dialog-close:hover {
  background-color: rgba(0, 0, 0, 0.1);
  color: #2c3e50;
}

.dialog-body {
  padding: 20px;
  flex: 1;
  overflow-y: auto;
}

.form-group {
  margin-bottom: 20px;
}

.form-group label {
  display: block;
  margin-bottom: 8px;
  font-weight: 600;
  color: #2c3e50;
  font-size: 14px;
}

.form-input,
.form-textarea {
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #dcdfe6;
  border-radius: 6px;
  font-size: 14px;
  font-family: inherit;
  transition: border-color 0.3s ease;
  box-sizing: border-box;
}

.form-input:focus,
.form-textarea:focus {
  outline: none;
  border-color: #ffd700;
  box-shadow: 0 0 0 2px rgba(255, 215, 0, 0.2);
}

.form-textarea {
  resize: vertical;
  min-height: 100px;
}

.dialog-footer {
  padding: 16px 20px;
  border-top: 1px solid #e4e7ed;
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  border-radius: 0 0 12px 12px;
}

.btn-secondary {
  background-color: #909399;
  border-color: #909399;
  color: white;
}

.btn-secondary:hover:not(:disabled) {
  background-color: #7a7e83;
  border-color: #7a7e83;
}

.gift-info {
  text-align: center;
  padding: 20px;
}

.gift-info p {
  font-size: 16px;
  color: #2c3e50;
  margin: 0;
}

.gift-info strong {
  color: #e6a23c;
  font-weight: 600;
}
</style>
